import { Block, Card, Button, Icon, Label, Input, OutsideClick, Loader, Text, Row, Column } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useMemo, useState } from 'react';
import T from 'Components/Helpers/translate';
import './RelationSelect.scss';
import { SlideFade } from 'App/components/animations';
import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import logError from 'App/babel/helpers/logError';
import Relation, { AvailableRelationConfig } from 'App/resources/Model/EntityRelation';
import { fetch, fetchCrossCompany, getRelations } from './helpers/fetchHelpers';
import RelationRow from './RelationRow';
import mapRelations, {
	activityRelationMapper,
	opportunityRelationMapper,
	appointmentRelationMapper,
	projectPlanRelationMapper,
	ticketRelationMapper,
	agreementRelationMapper
} from './helpers/relationMappers';
import RelatedRelations from './RelatedRelations';
import moment from 'moment';
import SelectedRelations from './SelectedRelations';
import Activity from 'App/resources/Model/Activity';
import Appointment from 'App/resources/Model/Appointment';
import Order from 'App/resources/Model/Order';
import ProjectPlan from 'App/resources/Model/ProjectPlan';
import { useFeatureAvailable, Feature, useSoftDeployAccess } from 'App/components/hooks/featureHelper';
import Ticket from 'App/resources/Model/Ticket';
import Agreement from 'App/resources/Model/Agreement';
import EventBus from 'App/services/EventBus';
import useIsFirstRender from 'App/components/hooks/useIsFirstRender';
import {
	RelationListItem,
	isInList,
	listToRelations,
	removeFromList,
	removeTypesFromList,
	toggleInList
} from './helpers/relationList';

export type OnChangeValue = {
	[key: string]: Relation['id'] | null | undefined;
};

const NO_RELATIONS: RelationListItem[] = [];

type Props = Omit<React.ComponentProps<typeof Block>, 'onChange'> & {
	onChange: (value: OnChangeValue) => void;
	onClose?: () => void;
	userId: number;
	client?: Pick<Client, 'id' | 'name'> | null;
	contact?: Pick<Contact, 'id' | 'name'> | null;
	opportunityId?: number | null;
	appointmentId?: number | null;
	activityId?: number | null;
	projectPlanId?: number | null;
	ticketId?: number | null;
	agreementId?: number | null;
	disabled?: boolean;
	inputRef?: (r: HTMLInputElement) => void;
	onInputBlur?: () => void;
	label?: string;
	autofocus?: boolean;
	autofocusDelay?: number;
	availableEntities?: AvailableRelationConfig;
	multiple?: boolean;
	disableOutsideClick?: boolean;
	openedFromDropdown?: boolean;
	onFetchDone?: () => void;
	hideRelated?: boolean;
	filterOutRelations?: Pick<Relation, 'type' | 'id'>[];
	filterOnUserId?: boolean;
	activity?: Activity;
	appointment?: Appointment;
	opportunity?: Order;
	projectPlan?: ProjectPlan;
	ticket?: Ticket;
	agreement?: Agreement;
	staticRelations?: Partial<AvailableRelationConfig>;
	showNoRelationsFound?: boolean;
	enableCrossCompany?: boolean;
	relationList?: RelationListItem[];
	onRelationListChange?: (relations: RelationListItem[]) => void;
};

const RelationSelect = ({
	client,
	contact,
	userId,
	onChange,
	onClose = () => {},
	opportunityId,
	appointmentId,
	activityId,
	projectPlanId,
	ticketId,
	agreementId,
	disabled = false,
	inputRef,
	onInputBlur,
	label,
	availableEntities = {
		order: false,
		opportunity: true,
		activity: true,
		appointment: true,
		projectPlan: true,
		ticket: true,
		agreement: true
	},
	multiple = true,
	autofocus = false,
	autofocusDelay = 0,
	disableOutsideClick = false,
	openedFromDropdown = false,
	onFetchDone,
	hideRelated,
	filterOutRelations = [],
	filterOnUserId = true,
	activity: initialActivity,
	appointment: initialAppointment,
	opportunity: initialOpportunity,
	ticket: initialTicket,
	projectPlan: initialProjectPlan,
	agreement: initialAgreement,
	staticRelations,
	showNoRelationsFound = false,
	enableCrossCompany = false,
	relationList = NO_RELATIONS,
	onRelationListChange,
	...props
}: Props) => {
	const classes = new BemClass('RelationSelect');
	const [relations, setRelations] = useState<Relation[]>([]);
	const [crossCompanyRelations, setCrossCompanyRelations] = useState<Relation[]>([]);
	const [open, setOpen] = useState(false);
	const [isFirstFetch, setIsFirstFetch] = useState(true);
	const [searching, setSearching] = useState(false);
	const mainRef = React.useRef<HTMLDivElement>();
	const [search, setSearch] = useState('');
	const hasMultipleRelations = useSoftDeployAccess('MULTIPLE_RELATIONS');
	const listMode = hasMultipleRelations && !!onRelationListChange;
	const [pickedRelations, setPickedRelations] = useState<Relation[]>([]);
	const isFirstRender = useIsFirstRender();
	const hasSelectedValues = listMode
		? relationList.length > 0
		: [appointmentId, opportunityId, activityId, projectPlanId, ticketId, agreementId].some(entity => !!entity);
	const hasProjectPlanAccess = useSoftDeployAccess('PROJECT_PLAN');
	const hasProjectPlanFeature = useFeatureAvailable(Feature.PROJECT_PLAN);
	const hasProjectPlan = hasProjectPlanAccess || hasProjectPlanFeature;

	const [selectedOpportunity, setSelectedOpportunity] = useState<Relation | null>(null);
	const [selectedAppointment, setSelectedAppointment] = useState<Relation | null>(null);
	const [selectedActivity, setSelectedActivity] = useState<Relation | null>(null);
	const [selectedProjectPlan, setSelectedProjectPlan] = useState<Relation | null>(null);
	const [selectedTicket, setSelectedTicket] = useState<Relation | null>(null);
	const [selectedAgreement, setSelectedAgreement] = useState<Relation | null>(null);

	const existingRelations = useMemo(
		() => ({
			opportunityId,
			appointmentId,
			activityId,
			projectPlanId,
			ticketId,
			agreementId
		}),
		[]
	);

	// Reset certain selected relations when userId changes, don't blindly add more relations to this (Jira issue: IM-1500)
	useEffect(() => {
		if (listMode) {
			if (isFirstRender) return;
			const kept = removeTypesFromList(relationList, ['opportunity', 'appointment', 'activity']);
			if (kept.length !== relationList.length) {
				onRelationListChange?.(kept);
			}
			return;
		}
		if (!selectedActivity && !selectedAppointment && !selectedOpportunity) return;
		const value: OnChangeValue = {
			opportunityId: null,
			appointmentId: null,
			activityId: null
		};
		onChange(value);
		setSelectedOpportunity(null);
		setSelectedAppointment(null);
		setSelectedActivity(null);
	}, [userId]);

	useEffect(() => {
		if (initialOpportunity) {
			setSelectedOpportunity(opportunityRelationMapper(initialOpportunity, contact));
		} else {
			setSelectedOpportunity(relations.find(rel => rel.id === opportunityId) ?? null);
		}
	}, [opportunityId]);

	useEffect(() => {
		if (initialAppointment) {
			setSelectedAppointment(appointmentRelationMapper(initialAppointment, userId, contact));
		} else {
			setSelectedAppointment(relations.find(rel => rel.id === appointmentId) ?? null);
		}
	}, [appointmentId]);

	useEffect(() => {
		if (initialActivity) {
			setSelectedActivity(activityRelationMapper(initialActivity, userId, contact));
		} else {
			setSelectedActivity(relations.find(rel => rel.id === activityId) ?? null);
		}
	}, [activityId]);

	useEffect(() => {
		if (initialProjectPlan) {
			const mapped = projectPlanRelationMapper(initialProjectPlan, contact);
			setSelectedProjectPlan(mapped);
		} else {
			setSelectedProjectPlan(relations.find(rel => rel.id === projectPlanId) ?? null);
		}
	}, [projectPlanId]);

	useEffect(() => {
		if (initialTicket) {
			setSelectedTicket(ticketRelationMapper(initialTicket, userId, contact));
		} else {
			setSelectedTicket(relations.find(rel => rel.id === ticketId) ?? null);
		}
	}, [ticketId]);

	useEffect(() => {
		if (initialAgreement) {
			setSelectedAgreement(agreementRelationMapper(initialAgreement, contact));
		} else {
			setSelectedAgreement(relations.find(rel => rel.id === agreementId) ?? null);
		}
	}, [agreementId]);

	useEffect(() => {
		if (!opportunityId && !appointmentId && !activityId && !projectPlanId && !ticketId && !agreementId) return;
		let fetchSelectedPromise: ReturnType<typeof getRelations> | null = null;
		fetchSelectedPromise = getRelations(
			initialOpportunity ? undefined : opportunityId,
			initialAppointment ? undefined : appointmentId,
			initialActivity ? undefined : activityId,
			initialProjectPlan ? undefined : projectPlanId,
			initialTicket ? undefined : ticketId,
			initialAgreement ? undefined : agreementId
		);

		fetchSelectedPromise?.promise
			.then(([opportunity, appointment, activity, projectPlan, ticket, agreement]) => {
				const selectedRelations = mapRelations(
					[initialOpportunity ?? opportunity],
					[initialAppointment ?? appointment],
					[initialActivity ?? activity],
					[initialProjectPlan ?? projectPlan],
					[initialTicket ?? ticket],
					[initialAgreement ?? agreement],
					userId,
					contact
				);
				setSelectedOpportunity(selectedRelations.find(rel => rel.id === opportunityId) ?? null);
				setSelectedAppointment(selectedRelations.find(rel => rel.id === appointmentId) ?? null);
				setSelectedActivity(selectedRelations.find(rel => rel.id === activityId) ?? null);
				setSelectedProjectPlan(selectedRelations.find(rel => rel.id === projectPlanId) ?? null);
				setSelectedTicket(selectedRelations.find(rel => rel.id === ticketId) ?? null);
				setSelectedAgreement(selectedRelations.find(rel => rel.id === agreementId) ?? null);
			})
			.catch(err => logError(err, 'Failed to set selected relations'));

		return () => {
			fetchSelectedPromise?.cancel();
		};
	}, []);

	useEffect(() => {
		let fetchPromise: ReturnType<typeof fetch> | null = null,
			crossCompanyPromise: ReturnType<typeof fetchCrossCompany> | null = null,
			debounce: ReturnType<typeof setTimeout> | null = null;

		if (!hasProjectPlan) {
			availableEntities.projectPlan = false;
		}

		if (client?.id) {
			setSearching(true);
			debounce = setTimeout(() => {
				fetchPromise = fetch(search, userId, client?.id || null, availableEntities, filterOnUserId);
				fetchPromise.promise
					.then(([opportunities, appointments, activities, projectPlans, tickets, agreements]) => {
						const relations = mapRelations(
							opportunities,
							appointments,
							activities,
							projectPlans,
							tickets,
							agreements,
							userId,
							contact
						).filter(
							item =>
								!filterOutRelations.some(
									filterItem => filterItem.id === item.id && filterItem.type === item.type
								)
						);
						setRelations(relations);
						if (isFirstFetch) {
							setIsFirstFetch(false);
							onFetchDone?.();
						}
					})
					.catch(err => logError(err, 'Failed to fetch appointment & opportunity'))
					.finally(() => {
						if (!enableCrossCompany || !search) {
							setSearching(false);
						}
					});

				if (enableCrossCompany && !search) {
					setCrossCompanyRelations([]);
				}

				if (enableCrossCompany && search) {
					crossCompanyPromise = fetchCrossCompany(search, userId, client.id, availableEntities);
					crossCompanyPromise.promise
						.then(([opportunities, appointments, activities, projectPlans, tickets, agreements]) => {
							const mapped = [
								...opportunities.map(item =>
									opportunityRelationMapper(item, contact, item.client?.name)
								),
								...appointments.map(item =>
									appointmentRelationMapper(item, userId, contact, item.client?.name)
								),
								...activities.map(item =>
									activityRelationMapper(item, userId, contact, item.client?.name)
								),
								...projectPlans.map(item =>
									projectPlanRelationMapper(item, contact, item.client?.name)
								),
								...tickets.map(item => ticketRelationMapper(item, userId, contact, item.client?.name)),
								...agreements.map(item => agreementRelationMapper(item, contact, item.client?.name))
							].filter(Boolean) as Relation[];
							setCrossCompanyRelations(mapped);
						})
						.catch(err => logError(err, 'Failed to fetch cross-company relations'))
						.finally(() => {
							setSearching(false);
						});
				}
			}, 300);
		}
		return () => {
			if (debounce) {
				clearTimeout(debounce);
			}
			if (fetchPromise) {
				fetchPromise.cancel();
			}
			if (crossCompanyPromise) {
				crossCompanyPromise.cancel();
			}
		};
	}, [
		client?.id,
		search,
		userId,
		opportunityId,
		appointmentId,
		activityId,
		projectPlanId,
		ticketId,
		agreementId,
		filterOnUserId
	]);

	useEffect(() => {
		let autofocusTimeout: NodeJS.Timeout;
		if (autofocus) {
			autofocusTimeout = setTimeout(() => setOpen(true), autofocusDelay);
		}

		return () => {
			if (autofocus && autofocusTimeout) {
				clearTimeout(autofocusTimeout);
			}
		};
	}, [autofocus]);

	const typeToEntityHandler = {
		appointment: {
			setter: setSelectedAppointment,
			mapper: (appointment: Appointment) => appointmentRelationMapper(appointment, userId, contact),
			id: appointmentId
		},
		opportunity: {
			setter: setSelectedOpportunity,
			mapper: (opportunity: Order) => opportunityRelationMapper(opportunity, contact),
			id: opportunityId
		},
		activity: {
			setter: setSelectedActivity,
			mapper: (activity: Activity) => activityRelationMapper(activity, userId, contact),
			id: activityId
		},
		projectPlan: {
			setter: setSelectedProjectPlan,
			mapper: (projectPlan: ProjectPlan) => projectPlanRelationMapper(projectPlan, contact),
			id: projectPlanId
		},
		ticket: {
			setter: setSelectedTicket,
			mapper: (ticket: Ticket) => ticketRelationMapper(ticket, userId, contact),
			id: ticketId
		},
		agreement: {
			setter: setSelectedAgreement,
			mapper: (agreement: Agreement) => agreementRelationMapper(agreement, contact),
			id: agreementId
		}
	};

	useEffect(() => {
		const onEvent = (event: any, eventEntity: any) => {
			const [eventType, eventAction]: [keyof AvailableRelationConfig, 'updated' | 'deleted'] =
				event.name.split('.');
			const type = eventType === 'order' ? 'opportunity' : eventType;

			if (type === 'udo' || !(type in typeToEntityHandler)) {
				return;
			}

			if (listMode) {
				const rel = { type, id: eventEntity.id };
				if (!isInList(relationList, rel)) {
					return;
				}
				if (eventAction === 'deleted') {
					onRelationListChange?.(removeFromList(relationList, rel));
				} else {
					const mapped = typeToEntityHandler[type].mapper(eventEntity);
					if (mapped) {
						setPickedRelations(current => [mapped, ...current]);
					}
				}
				return;
			}

			if (eventEntity.id === typeToEntityHandler[type].id) {
				const wasDeleted = eventAction === 'deleted';
				if (wasDeleted) {
					typeToEntityHandler[type].setter(null);
					setRelations(currentRelations =>
						currentRelations.filter(rel => rel.type === type && rel.id !== eventEntity.id)
					);

					const value: OnChangeValue = {
						opportunityId,
						appointmentId,
						activityId,
						projectPlanId,
						ticketId,
						agreementId
					};
					value[`${type}Id`] = null;
					onChange(value);
				} else {
					const mappedEventEntity = typeToEntityHandler[type].mapper(eventEntity);
					typeToEntityHandler[type].setter(mappedEventEntity);
					setRelations(currentRelations =>
						currentRelations.map(rel => {
							if (rel.type === type && rel.id === eventEntity.id) {
								return mappedEventEntity as Relation;
							}
							return rel;
						})
					);
				}
			}
		};

		const eventListeners = [...Object.keys(typeToEntityHandler), 'order'].flatMap(type => [
			`${type}.updated`,
			`${type}.deleted`
		]);
		const listeners = eventListeners.map(eventName => EventBus.on(eventName, onEvent));

		return () => {
			listeners.forEach(listener => listener());
		};
	}, [opportunityId, appointmentId, activityId, projectPlanId, ticketId, agreementId, listMode, relationList]);

	const toggleRow = (rel: Relation) => {
		if (listMode) {
			setPickedRelations(current => [rel, ...current]);
			onRelationListChange?.(toggleInList(relationList, rel));
			return;
		}
		if (!multiple && hasSelectedValues) return;
		const value: OnChangeValue = { opportunityId, appointmentId, activityId, projectPlanId, ticketId, agreementId };
		if (value[`${rel.type}Id`] === rel.id) {
			value[`${rel.type}Id`] = null;
		} else {
			value[`${rel.type}Id`] = rel.id;
		}
		onChange(value);

		// Close dropdown if we only expect one selection
		if (value[`${rel.type}Id`] === rel.id && !multiple) setOpen(false);
	};

	const relationRow = (rel: Relation | undefined) => {
		if (!rel) {
			return null;
		}
		return (
			<RelationRow
				key={`${rel.type}-${rel.id}`}
				relation={rel}
				toggleRow={toggleRow}
				selected={listMode ? isInList(relationList, rel) : undefined}
				selectedOpportunityId={opportunityId}
				selectedAppointmentId={appointmentId}
				selectedActivityId={activityId}
				selectedProjectPlanId={projectPlanId}
				selectedTicketId={ticketId}
				selectedAgreementId={agreementId}
			/>
		);
	};

	const groupedRelations = useMemo(() => {
		return relations.reduce<{
			withClient: Relation[];
			withContact: Relation[];
			withClientOld: Relation[];
			withContactOld: Relation[];
		}>(
			(res, relation) => {
				if (relation.withContact) {
					if (relation.date && moment(relation.date).diff(moment(), 'days') < -365)
						res.withContactOld.push(relation);
					else {
						res.withContact.push(relation);
					}
				} else {
					if (relation.date && moment(relation.date).diff(moment(), 'days') < -365)
						res.withClientOld.push(relation);
					else {
						res.withClient.push(relation);
					}
				}
				return res;
			},
			{ withContact: [], withClient: [], withContactOld: [], withClientOld: [] }
		);
	}, [relations]);

	const groupedCrossCompany = useMemo(() => {
		const groups: Record<string, { ongoing: Relation[]; old: Relation[] }> = {};
		for (const rel of crossCompanyRelations) {
			const name = rel.clientName || '';
			if (!groups[name]) {
				groups[name] = { ongoing: [], old: [] };
			}
			if (rel.date && moment(rel.date).diff(moment(), 'days') < -365) {
				groups[name].old.push(rel);
			} else {
				groups[name].ongoing.push(rel);
			}
		}
		return groups;
	}, [crossCompanyRelations]);

	const showRelated =
		!!relations.length ||
		!!crossCompanyRelations.length ||
		!!search ||
		initialActivity ||
		initialAppointment ||
		initialOpportunity ||
		!!selectedTicket ||
		!!selectedAgreement ||
		(listMode && !!relationList.length);

	if (!client || !showRelated) {
		if (openedFromDropdown || showNoRelationsFound) {
			return (
				<Text
					className={classes.elem('notFound').b()}
					italic
					color="grey-11"
					align={showNoRelationsFound ? 'left' : 'center'}
				>
					{T('relationDropdown.notFound')}
				</Text>
			);
		}
		return null;
	}

	const showInput =
		multiple ||
		listMode ||
		[appointmentId, opportunityId, activityId, projectPlanId, ticketId, agreementId].every(
			entity => entity === null
		);
	const selectedRelations = listMode
		? listToRelations(relationList, [...pickedRelations, ...relations])
		: ([
				selectedOpportunity,
				selectedAppointment,
				selectedActivity,
				selectedProjectPlan,
				selectedTicket,
				selectedAgreement
		  ].filter(r => r) as Relation[]);

	return (
		<Block {...props} className={classes.b()}>
			<OutsideClick
				targetRef={() => mainRef.current || null}
				listen={open && !disableOutsideClick}
				outsideClick={() => {
					setOpen(false);
					setSearch('');
					onClose();
					onInputBlur?.();
				}}
			>
				<div
					className={classes.elem('content').mod({ openedFromDropdown }).b()}
					ref={(r: HTMLInputElement) => (mainRef.current = r)}
				>
					{label ? <Label>{label}</Label> : null}
					<SlideFade visible={showInput} direction="top">
						<Input
							className={classes.elem('input').b()}
							inputRef={r => {
								if (r) {
									inputRef?.(r);
								}
							}}
							icon="search"
							placeholder={
								filterOnUserId
									? T('relateTo.searchAssigneesActivities')
									: T('relateTo.searchAllActivities')
							}
							value={search}
							onFocus={() => setOpen(true)}
							onChange={e => setSearch(e.target.value)}
							disabled={disabled}
						/>
					</SlideFade>

					{openedFromDropdown ? (
						<RelatedRelations
							opportunityId={existingRelations.opportunityId}
							appointmentId={existingRelations.appointmentId}
							activityId={existingRelations.activityId}
							projectPlanId={existingRelations.projectPlanId}
							ticketId={existingRelations.ticketId}
							agreementId={existingRelations.agreementId}
							selectedOpportunityId={opportunityId}
							selectedAppointmentId={appointmentId}
							selectedActivityId={activityId}
							selectedProjectPlanId={projectPlanId}
							selectedTicketId={ticketId}
							selectedAgreementId={agreementId}
							userId={userId}
							contact={contact}
							toggleRow={toggleRow}
						/>
					) : null}

					<SlideFade direction="top" bounce visible={open}>
						<Card className={openedFromDropdown ? '' : classes.elem('dropdown').b()}>
							<Block className={classes.elem('card').b()}>
								{searching ? <Loader size="sm" /> : null}
								{Object.values(groupedRelations).every(group => !group.length) &&
								!crossCompanyRelations.length &&
								!searching ? (
									<Row className="RelationRow">
										<Column>
											<Text italic color="grey-11" align="center">
												{T('noResult.generic')}
											</Text>
										</Column>
									</Row>
								) : null}
								{groupedRelations.withContact.length ? (
									<Label>{`${T('todo.ongoingWith')} ${contact?.name}`}</Label>
								) : null}
								{groupedRelations.withContact.map(relationRow)}
								{groupedRelations.withClient.length ? (
									<Label>{`${T('todo.ongoingWith')} ${client.name}`}</Label>
								) : null}
								{groupedRelations.withClient.map(relationRow)}
								{groupedRelations.withContactOld.length ? (
									<Label>{`${T('todo.moreThan1YearAgoWith')} ${contact?.name}`}</Label>
								) : null}
								{groupedRelations.withContactOld.map(relationRow)}
								{groupedRelations.withClientOld.length ? (
									<Label>{`${T('todo.moreThan1YearAgoWith')} ${client.name}`}</Label>
								) : null}
								{groupedRelations.withClientOld.map(relationRow)}
								{(
									Object.entries(groupedCrossCompany) as [
										string,
										{ ongoing: Relation[]; old: Relation[] }
									][]
								).map(([companyName, { ongoing, old }]) => (
									<React.Fragment key={companyName}>
										{ongoing.length ? (
											<>
												<Label>{`${T('todo.ongoingWith')} ${companyName}`}</Label>
												{ongoing.map(relationRow)}
											</>
										) : null}
										{old.length ? (
											<>
												<Label>{`${T('todo.moreThan1YearAgoWith')} ${companyName}`}</Label>
												{old.map(relationRow)}
											</>
										) : null}
									</React.Fragment>
								))}
							</Block>
							<Button
								block
								color={hasSelectedValues ? 'green' : 'super-light-green'}
								onClick={() => {
									onClose();
									setOpen(false);
									setSearch('');
								}}
							>
								<Icon name={hasSelectedValues ? 'check' : 'times'} space="mrs" />
								{hasSelectedValues ? T('default.save') : T('todo.noNotRelated')}
							</Button>
						</Card>
					</SlideFade>
					{!hideRelated && selectedRelations.length ? (
						<Block space="ptm">
							<SelectedRelations
								relations={selectedRelations}
								removeRelation={toggleRow}
								staticRelations={staticRelations}
							/>
						</Block>
					) : null}
				</div>
			</OutsideClick>
		</Block>
	);
};

export default RelationSelect;
